import { z } from 'zod';
import { emailSchema, isoTimestamp, roleSchema } from './common';

export const tenantStatusSchema = z.enum(['active', 'suspended']);
export type TenantStatus = z.infer<typeof tenantStatusSchema>;

export const tenantSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(
    /^[a-z0-9][a-z0-9-]{1,46}[a-z0-9]$/,
    'Slug must be 3–48 characters of lowercase letters, digits and hyphens, and must not start or end with a hyphen',
  );

export const tenantDtoSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  status: tenantStatusSchema,
  createdAt: isoTimestamp,
});
export type TenantDto = z.infer<typeof tenantDtoSchema>;

export const createTenantRequestSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(100),
  slug: tenantSlugSchema,
  ownerEmail: emailSchema,
});
export type CreateTenantRequest = z.infer<typeof createTenantRequestSchema>;

export const updateTenantRequestSchema = z.object({ status: tenantStatusSchema });

export const invitationDtoSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  role: roleSchema,
  expiresAt: isoTimestamp,
  createdAt: isoTimestamp,
});
export type InvitationDto = z.infer<typeof invitationDtoSchema>;

export const createdInvitationSchema = z.object({
  invitation: invitationDtoSchema,
  acceptUrl: z.string(),
});
export type CreatedInvitation = z.infer<typeof createdInvitationSchema>;

export const createTenantResponseSchema = z.object({
  tenant: tenantDtoSchema,
  ownerInvitation: createdInvitationSchema,
});
export type CreateTenantResponse = z.infer<typeof createTenantResponseSchema>;

/** Public mail providers: a tenant must not claim these, or every user of one would join it. */
export const FREE_MAIL_DOMAINS: ReadonlySet<string> = new Set([
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  'yahoo.com',
  'ymail.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'gmx.com',
  'zoho.com',
  'mail.com',
  'yandex.com',
]);

export const emailDomainSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(253)
  .regex(
    /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/,
    'Enter a domain such as example.com',
  )
  .refine((d) => !FREE_MAIL_DOMAINS.has(d), 'Public email providers cannot be used as a domain');

export const tenantDomainDtoSchema = z.object({ domain: z.string().nullable() });
export type TenantDomainDto = z.infer<typeof tenantDomainDtoSchema>;

export const updateTenantDomainRequestSchema = z.object({ domain: emailDomainSchema.nullable() });
export type UpdateTenantDomainRequest = z.infer<typeof updateTenantDomainRequestSchema>;
